<template>
  <div
    class="ui-select"
    :class="[
      `ui-select--${size}`,
      {
        'ui-select--open': isOpen,
        'ui-select--selected': !!selectedOption,
        'ui-select--error': error,
        'ui-select--disabled': disabled,
        'ui-select--view': viewOnly,
        'ui-select--bold': bold,
        'ui-select--color-info': color === 'info',
        'ui-select--color-danger': color === 'danger',
        'ui-select--color-warning': color === 'warning',
        'ui-select--color-success': color === 'success'
      }
    ]"
    :style="{ width }"
    :data-invalid-eval-anchor="invalidEvalAnchor || undefined"
    ref="selectRef"
  >
    <!-- 셀렉트 헤더 부분 (선택된 값 표시) -->
    <div
      class="ui-select__header"
      @click="toggleDropdown"
      @blur="handleHeaderBlur"
      :tabindex="disabled || viewOnly ? -1 : 0"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
    >
      <UiTooltip
        v-if="showSelectedLabelTooltip"
        :title="selectedOption.label"
        position="bottom-left"
        :fixed="true"
        :auto-flip="true"
        :z-index="selectedLabelTooltipZIndex"
        class="ui-select__value-tooltip"
      >
        <template #trigger>
          <div class="ui-select__value">
            <div class="ui-select__selected-content">
              <span ref="selectedTextRef" class="ui-select__selected-text">{{ selectedOption.label }}</span>
              <span v-if="selectedOption.color" class="ui-select__selected-icon" :style="{ backgroundColor: selectedOption.color }"></span>
            </div>
          </div>
        </template>
      </UiTooltip>
      <div v-else class="ui-select__value">
        <div v-if="selectedOption" class="ui-select__selected-content">
          <span ref="selectedTextRef" class="ui-select__selected-text">{{ selectedOption.label }}</span>
          <span v-if="selectedOption.color" class="ui-select__selected-icon" :style="{ backgroundColor: selectedOption.color }"></span>
        </div>
        <span v-else class="ui-select__placeholder">{{ resolvedPlaceholder }}</span>
      </div>
      <i class="ui-select__icon" :class="{ 'ui-select__icon--up': isOpen }"></i>
    </div>

    <!-- Portal을 사용한 드롭다운 (body에 렌더링) -->
    <Teleport to="body" v-if="isOpen">
      <div
        class="ui-select__dropdown"
        :class="{ 'ui-select__dropdown--up': dropdownDirection === 'up' }"
        :style="dropdownStyles"
        ref="dropdownRef"
        @mousedown.prevent
      >
        <div class="ui-select__options" role="listbox">
          <div
            v-for="option in normalizedOptions"
            :key="option.value"
            class="ui-select__option"
            :class="{
              'ui-select__option--selected': modelValue === option.value || String(modelValue) === String(option.value),
              'ui-select__option--focused':
                scrollToSelectedOnOpen &&
                isOpen &&
                (modelValue === option.value || String(modelValue) === String(option.value)),
            }"
            role="option"
            :aria-selected="modelValue === option.value || String(modelValue) === String(option.value)"
            :tabindex="scrollToSelectedOnOpen ? -1 : undefined"
            @click="selectOption(option)"
          >
            <span v-ellipsis-tooltip="option.label" class="ui-select__option-label">{{ option.label }}</span>
            <span v-if="option.color" class="ui-select__option-icon" :style="{ backgroundColor: option.color }"></span>
          </div>
          <div
            v-if="options.length === 0"
            class="ui-select__no-results"
            :class="{ 'ui-select__no-results--left': emptyTextAlign === 'left' }"
          >
            {{ t('common.uiSelect.noOptions') }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { UI_OVERLAY_DISMISS_EVENT } from '~/composables/useOverlayDismiss';
import UiTooltip from '~/components/common/UI/UiTooltip.vue';

const { t } = useI18n();
const LEGACY_SELECT_PLACEHOLDER = '선택하세요';

// 전역 이벤트 버스 생성
const SELECT_EVENT_KEY = 'select-dropdown-toggle';
const uniqueId = ref(`select-${Math.random().toString(36).substr(2, 9)}`);
const selectRef = ref(null);
const dropdownRef = ref(null);

// 드롭다운 위치 관련 상태
const dropdownDirection = ref('down');
const dropdownStyles = ref({});

/** nextTick 직후에는 레이아웃이 아직 확정되지 않아 getBoundingClientRect가 틀어질 수 있어, rAF 이후에 한 번 더 맞춘다 */
let dropdownPositionRafId = null;
let scrollToSelectedRafId = null;
let selectResizeObserver = null;

const cancelScheduledDropdownPosition = () => {
  if (dropdownPositionRafId != null) {
    cancelAnimationFrame(dropdownPositionRafId);
    dropdownPositionRafId = null;
  }
};

const cancelScheduledScrollToSelected = () => {
  if (scrollToSelectedRafId != null) {
    cancelAnimationFrame(scrollToSelectedRafId);
    scrollToSelectedRafId = null;
  }
};

const detachSelectResizeObserver = () => {
  selectResizeObserver?.disconnect();
  selectResizeObserver = null;
};

const attachSelectResizeObserver = () => {
  detachSelectResizeObserver();
  if (!selectRef.value || typeof ResizeObserver === 'undefined') return;
  selectResizeObserver = new ResizeObserver(() => {
    if (isOpen.value) calculateDropdownPosition();
  });
  selectResizeObserver.observe(selectRef.value);
};

const scheduleDropdownPosition = () => {
  cancelScheduledDropdownPosition();
  nextTick(() => {
    if (!isOpen.value) return;
    calculateDropdownPosition();
    dropdownPositionRafId = requestAnimationFrame(() => {
      dropdownPositionRafId = null;
      if (!isOpen.value) return;
      calculateDropdownPosition();
    });
  });
};

const props = defineProps({
  placeholder: {
    type: String,
    default: LEGACY_SELECT_PLACEHOLDER,
  },
  options: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  viewOnly: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'medium',
    validator: value => ['small', 'medium', 'large', 'xlarge'].includes(value),
  },
  width: {
    type: [String, Number],
    default: '100%',
  },
  // 드롭다운 최대 높이 제어
  maxHeight: {
    type: [String, Number],
    default: 200,
  },
  // 강제로 위쪽으로 열기
  forceUpward: {
    type: Boolean,
    default: false,
  },
  // Color
  color: {
    type: String,
    default: 'primary',
    validator: value => ['primary', 'info', 'danger', 'warning', 'success', ''].includes(value),
  },
  bold: {
    type: Boolean,
    default: false,
  },
  // target-value: options의 value 대신 사용할 속성명
  targetValue: {
    type: String,
    default: '',
  },
  // target-label: options의 label 대신 사용할 속성명
  targetLabel: {
    type: String,
    default: '',
  },
  /** 평가 유효성 실패 시 하이라이트 앵커 (`data-invalid-eval-anchor`) */
  invalidEvalAnchor: {
    type: String,
    default: '',
  },
  /** true: 바깥 스크롤 시 드롭다운 위치 이동 대신 닫기 (모달 내 목록 스크롤 등) */
  closeOnScroll: {
    type: Boolean,
    default: false,
  },
  /** true: 드롭다운 재오픈 시 선택된 옵션을 목록 상단에 맞춰 스크롤·포커스 */
  scrollToSelectedOnOpen: {
    type: Boolean,
    default: false,
  },
  /** 빈 옵션 안내 문구 정렬 */
  emptyTextAlign: {
    type: String,
    default: 'center',
    validator: value => ['left', 'center'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const resolvedPlaceholder = computed(() => {
  const p = props.placeholder;
  if (!p || p === LEGACY_SELECT_PLACEHOLDER) {
    return t('common.selectPlaceholder');
  }
  return p;
});

// 상태 관리
const isOpen = ref(false);

// options를 정규화하여 value와 label로 변환
const normalizedOptions = computed(() => {
  return props.options.map(option => {
    const normalized = { ...option };

    // target-value가 있으면 해당 값을 value에 매핑
    if (props.targetValue && option[props.targetValue] !== undefined) {
      normalized.value = option[props.targetValue];
    }

    // target-label이 있으면 해당 값을 label에 매핑
    if (props.targetLabel && option[props.targetLabel] !== undefined) {
      normalized.label = option[props.targetLabel];
    }

    return normalized;
  });
});

// 선택된 옵션 객체 — modelValue를 직접 참조해 외부에서 값이 복원될 때도 표시 동기화
const selectedOption = computed(() => {
  const current = props.modelValue;
  if (current == null) return undefined;
  return normalizedOptions.value.find(
    option => option.value === current || String(option.value) === String(current),
  );
});

const selectedTextRef = ref(null);
const isSelectedLabelTruncated = ref(false);
let selectedTextResizeObserver = null;

const checkSelectedLabelTruncation = () => {
  const el = selectedTextRef.value;
  isSelectedLabelTruncated.value = el ? el.scrollWidth > el.clientWidth : false;
};

const attachSelectedTextResizeObserver = () => {
  detachSelectedTextResizeObserver();
  if (!selectedTextRef.value || typeof ResizeObserver === 'undefined') return;
  selectedTextResizeObserver = new ResizeObserver(() => checkSelectedLabelTruncation());
  selectedTextResizeObserver.observe(selectedTextRef.value);
  if (selectRef.value) {
    selectedTextResizeObserver.observe(selectRef.value);
  }
};

const detachSelectedTextResizeObserver = () => {
  selectedTextResizeObserver?.disconnect();
  selectedTextResizeObserver = null;
};

const showSelectedLabelTooltip = computed(
  () =>
    !isOpen.value &&
    !props.disabled &&
    !props.viewOnly &&
    !!selectedOption.value &&
    isSelectedLabelTruncated.value,
);

watch([() => props.modelValue, () => props.options, isOpen], () => {
  nextTick(() => {
    checkSelectedLabelTruncation();
    attachSelectedTextResizeObserver();
  });
});

// 송기준 수정: 본문(940)은 사이드바보다 낮게 유지, 모달·UiFilterModal 안에서는 그 레이어보다 위로만 올려 옵션 목록이 가려지지 않게 함
const resolveDropdownZIndex = () => {
  const el = selectRef.value;
  if (!el) return 940;

  const filterPanel = el.closest('.filter-dropdown');
  if (filterPanel) {
    const z = parseInt(window.getComputedStyle(filterPanel).zIndex, 10);
    return (Number.isFinite(z) ? z : 10051) + 10;
  }

  const popup = el.closest('.ui-popup');
  if (popup) {
    let z = parseInt(window.getComputedStyle(popup).zIndex, 10);
    if (!Number.isFinite(z)) {
      const content = popup.querySelector('.ui-popup__content');
      if (content) {
        z = parseInt(window.getComputedStyle(content).zIndex, 10);
      }
    }
    const base = Number.isFinite(z) ? z : 1000;
    return base + 10;
  }

  // 우측 슬라이드 패널(.detail-panel 등) 위에 떠야 하는 경우 z-index 보정
  const slidePanel = el.closest('.detail-panel');
  if (slidePanel) {
    const z = parseInt(window.getComputedStyle(slidePanel).zIndex, 10);
    const base = Number.isFinite(z) ? z : 1000;
    return base + 10;
  }

  return 940;
};

/** UiFilterModal(10051) 등 오버레이 위에 툴팁이 가려지지 않도록 드롭다운과 동일 z-index 보정 */
const selectedLabelTooltipZIndex = computed(() => {
  if (!selectRef.value) return null;
  const overlayZ = resolveDropdownZIndex();
  return overlayZ > 1000 ? overlayZ : null;
});

// 드롭다운 위치 계산 함수
const calculateDropdownPosition = () => {
  if (!isOpen.value || !selectRef.value) return;

  const selectRect = selectRef.value.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  // 송기준 수정: Teleport + fixed 드롭다운이 상단 고정 헤더(.header) 위로 겹치지 않도록, 위쪽 가용 높이에서 헤더 하단까지 제외
  const headerEl = document.querySelector('.header');
  const headerBottom = headerEl ? headerEl.getBoundingClientRect().bottom : 0;

  // 예상 드롭다운 높이 계산
  const estimatedDropdownHeight = Math.min(
    props.options.length * 40 + 16, // 각 옵션 높이 + 패딩
    typeof props.maxHeight === 'number' ? props.maxHeight : parseInt(props.maxHeight),
  );

  // 아래쪽 공간과 위쪽 공간 계산
  const spaceBelow = windowHeight - selectRect.bottom - 8; // 8px 마진
  // 송기준 수정: spaceAbove — 셀렉트 상단 ~ 헤더 아래 경계 사이만 위로 펼침 허용
  const spaceAbove = Math.max(0, selectRect.top - 8 - headerBottom);

  // 드롭다운 방향 결정
  let direction = 'down';

  if (props.forceUpward) {
    direction = 'up';
  } else if (spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow) {
    direction = 'up';
  }

  dropdownDirection.value = direction;

  // 위치 스타일 계산
  const styles = {
    position: 'fixed',
    left: `${selectRect.left}px`,
    width: `${selectRect.width}px`,
    zIndex: resolveDropdownZIndex(),
    maxHeight: `${props.maxHeight}px`,
  };

  if (direction === 'up') {
    styles.bottom = `${windowHeight - selectRect.top + 4}px`;
    styles.maxHeight = `${Math.min(spaceAbove, typeof props.maxHeight === 'number' ? props.maxHeight : parseInt(props.maxHeight))}px`;
  } else {
    styles.top = `${selectRect.bottom + 4}px`;
    styles.maxHeight = `${Math.min(spaceBelow, typeof props.maxHeight === 'number' ? props.maxHeight : parseInt(props.maxHeight))}px`;
  }

  // 좌우 화면 벗어남 방지
  if (selectRect.right > windowWidth) {
    styles.right = '8px';
    delete styles.left;
  }

  dropdownStyles.value = styles;
};

/** 드롭다운 재오픈 시 선택된 옵션을 목록 상단에 맞춤 (scrollToSelectedOnOpen일 때만) */
const scrollToSelectedOption = () => {
  if (!props.scrollToSelectedOnOpen) return;

  cancelScheduledScrollToSelected();
  nextTick(() => {
    scrollToSelectedRafId = requestAnimationFrame(() => {
      scrollToSelectedRafId = null;
      if (!isOpen.value || !dropdownRef.value) return;

      const selectedEl = dropdownRef.value.querySelector('.ui-select__option--selected');
      if (!selectedEl) return;

      const optionsEl = dropdownRef.value.querySelector('.ui-select__options');
      if (optionsEl) {
        dropdownRef.value.scrollTop = Math.max(0, selectedEl.offsetTop - optionsEl.offsetTop);
      } else {
        selectedEl.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
      if (typeof selectedEl.focus === 'function') {
        selectedEl.focus({ preventScroll: true });
      }
    });
  });
};

// 드롭다운 토글
const toggleDropdown = () => {
  if (props.disabled || props.viewOnly) return;

  if (!isOpen.value) {
    // 다른 셀렉트 드롭다운을 닫기 위해 이벤트 발생
    window.dispatchEvent(
      new CustomEvent(SELECT_EVENT_KEY, {
        detail: { id: uniqueId.value },
      }),
    );
  }

  isOpen.value = !isOpen.value;
};

// 외부에서 드롭다운 강제 열기 + 포커스 (예: 필수 선택 유도)
const open = () => {
  if (props.disabled || props.viewOnly || isOpen.value) return;
  // 다른 셀렉트 드롭다운 닫기
  window.dispatchEvent(
    new CustomEvent(SELECT_EVENT_KEY, {
      detail: { id: uniqueId.value },
    }),
  );
  isOpen.value = true;
  selectRef.value?.querySelector('.ui-select__header')?.focus();
};

defineExpose({ open });

// 옵션 선택
const selectOption = option => {
  if (props.disabled || props.viewOnly) return;
  emit('update:modelValue', option.value);
  emit('change', option.value);
  isOpen.value = false;
};

// 다른 셀렉트가 열릴 때 현재 셀렉트를 닫는 이벤트 핸들러
const handleOtherSelectOpen = event => {
  if (event.detail.id !== uniqueId.value && isOpen.value) {
    isOpen.value = false;
  }
};

// 문서 클릭(캡처): 필터 모달 등 부모의 @click.stop 으로 버블이 끊겨도 바깥 클릭 시 드롭다운 닫힘
const handleDocumentClick = event => {
  if (isOpen.value && selectRef.value && !selectRef.value.contains(event.target) && dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    isOpen.value = false;
  }
};

const handleHeaderBlur = () => {
  setTimeout(() => {
    if (!isOpen.value) return;
    const active = document.activeElement;
    if (selectRef.value?.contains(active) || dropdownRef.value?.contains(active)) return;
    isOpen.value = false;
  }, 0);
};

const handleOverlayDismiss = () => {
  if (isOpen.value) {
    isOpen.value = false;
  }
};

// ESC 키로 드롭다운 닫기
const handleKeydown = event => {
  if (event.key === 'Escape' && isOpen.value) {
    isOpen.value = false;
    selectRef.value?.querySelector('.ui-select__header')?.focus();
  }
};

// 드롭다운 열림: 위치 스케줄·옵저버·윈도우 리스너 / 닫힘: 정리
const handleScrollWhileOpen = event => {
  if (!isOpen.value) return;
  if (props.closeOnScroll) {
    if (dropdownRef.value?.contains(event.target)) return;
    isOpen.value = false;
    return;
  }
  calculateDropdownPosition();
};

watch(isOpen, open => {
  if (open) {
    scheduleDropdownPosition();
    scrollToSelectedOption();
    attachSelectResizeObserver();
    window.addEventListener('resize', calculateDropdownPosition);
    window.addEventListener('scroll', handleScrollWhileOpen, true);
  } else {
    cancelScheduledDropdownPosition();
    cancelScheduledScrollToSelected();
    detachSelectResizeObserver();
    window.removeEventListener('resize', calculateDropdownPosition);
    window.removeEventListener('scroll', handleScrollWhileOpen, true);
  }
});

// 이벤트 리스너 등록 및 해제
onMounted(() => {
  window.addEventListener(SELECT_EVENT_KEY, handleOtherSelectOpen);
  window.addEventListener(UI_OVERLAY_DISMISS_EVENT, handleOverlayDismiss);
  document.addEventListener('click', handleDocumentClick, true);
  document.addEventListener('keydown', handleKeydown);
  nextTick(() => {
    checkSelectedLabelTruncation();
    attachSelectedTextResizeObserver();
  });
});

onUnmounted(() => {
  window.removeEventListener(SELECT_EVENT_KEY, handleOtherSelectOpen);
  window.removeEventListener(UI_OVERLAY_DISMISS_EVENT, handleOverlayDismiss);
  document.removeEventListener('click', handleDocumentClick, true);
  document.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('resize', calculateDropdownPosition);
  window.removeEventListener('scroll', handleScrollWhileOpen, true);
  cancelScheduledDropdownPosition();
  cancelScheduledScrollToSelected();
  detachSelectResizeObserver();
  detachSelectedTextResizeObserver();
});
</script>

<style lang="scss" scoped>
// 애니메이션 키프레임
@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes dropdownFadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ui-select {
  position: relative;
  font-family: $font-family;
  border-radius: $border-radius-sm;
  border: 1px solid $border-color-light;
  background: var(--color-gray-0, #fff);
  color: var(--color-gray-40, #8a949e);
  transition: all $transition-normal ease;
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;

  // 값이 입력되었을 때 스타일
  &--selected {
    border: 1px solid $border-color-filled;
  }

  // 아이콘 스타일
  &__icon {
    flex-shrink: 0;
    width: 12px;
    height: 8px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M11 1.5L6 6.5L1 1.5' stroke='%23464C53' stroke-width='2'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    transition: transform 0.2s ease;

    &--up {
      transform: rotate(180deg);
    }
  }

  // 헤더 스타일
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    box-sizing: border-box;
    width: inherit;
    min-width: 0;
    &:focus {
      outline: none;
    }
  }

  // 값 스타일
  &__value,
  &__value-tooltip {
    flex: 1 1 0;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value-tooltip :deep(.ui-tooltip-wrapper) {
    display: block;
    width: 100%;
    min-width: 0;
  }

  &__placeholder {
    color: $placeholder-color;
    font-family: $placeholder-font-family;
    font-size: $ui-font-md;
    font-style: normal;
    font-weight: $placeholder-font-weight;
    line-height: $placeholder-line-height;
  }

  &__selected-text {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--color-gray-70, #464c53);
  }

  &__selected-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  // 상태 변형
  &--open {
    border: 1px solid var(--color-system-b30, #0084ff);
    color: var(--color-gray-70, #464c53);
  }

  &--selected {
    border: 1px solid var(--color-gray-60, #58616a);
    color: var(--color-gray-70, #464c53);
  }

  &--error {
    border: 1px solid var(--color-system-r30, #f30);
    color: var(--color-gray-70, #464c53);
  }

  &--disabled {
    border: 1px solid var(--color-gray-20, #cdd1d5);
    background: var(--color-gray-10, #e6e8ea);
    color: var(--color-gray-30, #b1b8be);
    cursor: not-allowed;

    .ui-select__header {
      pointer-events: none;
    }
  }

  // viewOnly 스타일을 UiInput readonly와 동일하게 수정
  &--view {
    .ui-select__header {
      background: var(--color-gray-10, #e6e8ea); // 읽기 전용 배경
      border: 1px solid $border-color-light;
      color: var(--color-gray-60, #58616a);
      cursor: default;
      pointer-events: none;
    }
  }

  // 크기 변형 - Small (32px)
  &--small {
    .ui-select__header {
      height: $ui-height-sm; // 32px
      padding: $ui-padding-sm;
      box-sizing: border-box;
    }

    .ui-select__selected-text,
    .ui-select__placeholder {
      font-size: $ui-font-sm;
    }
  }

  // 크기 변형 - Medium (40px)
  &--medium {
    .ui-select__header {
      height: $ui-height-md; // 40px
      padding: $ui-padding-md;
      box-sizing: border-box;
    }

    .ui-select__selected-text,
    .ui-select__placeholder {
      font-size: $ui-font-md;
    }
  }

  // 크기 변형 - Large (48px)
  &--large {
    .ui-select__header {
      height: $ui-height-lg; // 48px
      padding: $ui-padding-lg;
      box-sizing: border-box;
    }

    .ui-select__placeholder,
    .ui-select__selected-text {
      font-size: $ui-font-lg;
    }
  }

  // 크기 변형 - XLarge (56px)
  &--xlarge {
    .ui-select__header {
      height: $ui-height-xl; // 56px
      padding: $ui-padding-xl;
      box-sizing: border-box;
    }

    .ui-select__selected-text,
    .ui-select__placeholder {
      font-size: $ui-font-xl;
    }
  }

  // 반응형 스타일
  @media (max-width: 768px) {
    &--small {
      .ui-select__header {
        padding: $ui-padding-sm;
      }
    }

    &--medium {
      .ui-select__header {
        padding: $ui-padding-md;
      }
    }

    &--large {
      .ui-select__header {
        padding: $ui-padding-lg;
      }
    }

    &--xlarge {
      .ui-select__header {
        padding: $ui-padding-xl;
      }
    }
  }
}
</style>

<!-- 전역 스타일 (드롭다운은 body에 렌더링되므로 scoped 적용 안됨) -->
<style lang="scss">
.ui-select__dropdown {
  background: #fff;
  border: 1px solid var(--color-gray-20, #cdd1d5);
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  overflow-y: auto;
  animation: dropdownFadeIn 0.15s ease;

  &--up {
    animation: dropdownFadeInUp 0.15s ease;
  }

  // 스크롤바 스타일링
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }
}

.ui-select__options {
  width: 100%;
  padding: 4px 0;
}

.ui-select__option {
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  font-size: 14px;
  line-height: 1.4;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background-color: var(--color-gray-10, #e6e8ea);
  }

  &--selected {
    background-color: var(--color-gray-10, #e6e8ea);
    color: var(--color-system-b30, #0084ff);
    font-weight: 500;
  }

  &--focused {
    outline: 2px solid var(--color-system-b30, #0084ff);
    outline-offset: -2px;
  }
}

.ui-select__option-label {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ui-select__option-icon {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-left: 8px;
  flex-shrink: 0;
}


.ui-select__selected-icon {
  width: 12px;
  height: 12px;
  margin-right: 5px;
  border-radius: 50%;
  margin-left: 8px;
  flex-shrink: 0;
}

.ui-select__no-results {
  padding: 12px;
  text-align: center;
  color: var(--color-gray-40, #8a949e);
  font-size: 14px;
  font-weight: 500;

  &--left {
    text-align: left;
  }
}

.ui-select--color-info {
  .ui-select__selected-text {
    color: var(--color-system-info, #007bff); // 시스템 블루 색상으로 설정
  }
}

.ui-select--color-danger {
  .ui-select__selected-text {
    color: var(--color-system-danger, #dc3545); // 시스템 레드 색상으로 설정
  }
}

.ui-select--color-warning {
  .ui-select__selected-text {
    color: var(--color-system-warning, #ffc107); // 시스템 노란색으로 설정
  }
}

.ui-select--color-success {
  .ui-select__selected-text {
    color: var(--color-system-success, #28a745); // 시스템 초록색으로 설정
  }
}

.ui-select--bold {
  .ui-select__selected-text {
    font-weight: bold;
  }
}
</style>
